import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  ArrowRightIcon,
  ButtonV2,
  EyeClosedIcon,
  EyeIcon,
  IconButtonV2,
  OverlayTriggerTooltip,
  PencilV2Icon,
  TrashIcon,
} from '@utilities';

import styles from './_index.module.scss';

const TopicCardFooter = ({
  footerMessage = null,
  footerStatus = null,
  handleToggleVisibility,
  isExcluded,
  isLoading,
  isPreviewActive = false,
  isPreviewCard = false,
  shouldShowExcludedTopics = false,
  topicControls = null,
}) => {
  const buttonText = () => {
    if (isPreviewCard) {
      return isPreviewActive ? 'Close' : 'Preview';
    }
    return isExcluded ? 'Include' : 'Exclude';
  };

  const resolvedFooterMessage = () => {
    if (isPreviewCard) {
      return isPreviewActive ? (
        <span className={styles['topic-card-footer-message']}>
          This is a preview, not an actual analysis. Reach out to your account team for more
          information.
        </span>
      ) : (
        <span className={styles['topic-card-footer-message']}>
          <strong>Excluded</strong> This question is answered utilizing data outside of your current
          subscription.
        </span>
      );
    }
    if (footerMessage) {
      return (
        <span className={styles['topic-card-footer-message']}>
          <strong>Excluded </strong>
          {footerMessage}
        </span>
      );
    }
    return null;
  };

  const renderVisibilityButton = ({ ariaLabel, icon, isActive, isDisabled, tooltipContent }) => (
    <OverlayTriggerTooltip content={tooltipContent}>
      <span className={styles['topic-card-footer-visibility-button-wrapper']}>
        <button
          aria-label={ariaLabel}
          aria-pressed={isActive}
          className={classNames(styles['topic-card-footer-visibility-button'], {
            [styles['is-active']]: isActive,
          })}
          data-testid={`topic-card-footer-${ariaLabel}-button`}
          disabled={isDisabled}
          onClick={handleToggleVisibility}
          type="button"
        >
          <span className={styles['topic-card-footer-visibility-icon']}>{icon}</span>
        </button>
      </span>
    </OverlayTriggerTooltip>
  );

  const renderDefaultButton = () =>
    isPreviewCard ? (
      <ButtonV2
        className={classNames(styles['topic-card-footer-exclude-button'], {
          [styles['is-preview']]: isPreviewCard,
          [styles['is-preview-active']]: isPreviewActive,
        })}
        disabled={isLoading}
        onClick={handleToggleVisibility}
      >
        {buttonText()}
      </ButtonV2>
    ) : footerMessage ? null : shouldShowExcludedTopics ? (
      <div
        className={classNames(styles['topic-card-footer-visibility-controls'], {
          [styles['is-excluded']]: isExcluded,
        })}
        data-testid="visibility-controls"
      >
        {renderVisibilityButton({
          ariaLabel: 'include',
          icon: <EyeIcon />,
          isActive: !isExcluded,
          isDisabled: isLoading || !isExcluded,
          tooltipContent: 'Include',
        })}
        {renderVisibilityButton({
          ariaLabel: 'exclude',
          icon: <EyeClosedIcon />,
          isActive: isExcluded,
          isDisabled: isLoading || isExcluded,
          tooltipContent: 'Exclude',
        })}
      </div>
    ) : null;

  const renderControlButtons = () => {
    if (!topicControls) return null;

    const { primaryAction = null, secondaryActions = [] } = topicControls;
    const resolvedPrimary = primaryAction?.label
      ? {
          disabled: primaryAction?.disabled,
          label: primaryAction.label,
          onClick: primaryAction?.onClick,
          tooltip: primaryAction?.tooltip,
        }
      : null;

    const resolvedSecondary = Array.isArray(secondaryActions)
      ? secondaryActions.filter((action) => action?.label)
      : [];

    const removeAction =
      resolvedPrimary?.label === 'Remove'
        ? resolvedPrimary
        : resolvedSecondary.find((action) => action?.label === 'Remove') || null;
    const editAction =
      resolvedPrimary?.label === 'Edit'
        ? resolvedPrimary
        : resolvedSecondary.find((action) => action?.label === 'Edit') || null;
    const moveUpAction = resolvedSecondary.find((action) => action?.label === 'Move Up') || null;
    const moveDownAction =
      resolvedSecondary.find((action) => action?.label === 'Move Down') || null;
    const textActions = [
      ...(resolvedPrimary && !['Edit', 'Remove'].includes(resolvedPrimary?.label)
        ? [resolvedPrimary]
        : []),
      ...resolvedSecondary.filter(
        (action) => !['Edit', 'Move Down', 'Move Up', 'Remove'].includes(action?.label)
      ),
    ];

    return (
      <>
        {textActions.map((action) => (
          <ButtonV2
            disabled={action?.disabled}
            key={action.label}
            onClick={action?.onClick}
            tooltip={action?.tooltip}
          >
            {action.label}
          </ButtonV2>
        ))}
        {editAction ? (
          <IconButtonV2
            aria-label="Edit"
            className={styles['topic-card-footer-remove-button']}
            data-testid="topic-card-footer-edit-button"
            disabled={editAction?.disabled}
            icon={<PencilV2Icon />}
            onClick={editAction?.onClick}
            size="small"
            tooltip={editAction?.tooltip || 'Edit'}
            tooltipProps={{ placement: 'top' }}
            variant="default"
          />
        ) : null}
        {removeAction ? (
          <IconButtonV2
            aria-label="Remove"
            className={styles['topic-card-footer-remove-button']}
            data-testid="topic-card-footer-remove-button"
            disabled={removeAction?.disabled}
            icon={<TrashIcon />}
            onClick={removeAction?.onClick}
            size="small"
            tooltip={removeAction?.tooltip || 'Remove'}
            tooltipProps={{ placement: 'top' }}
            variant="default"
          />
        ) : null}
        {moveUpAction || moveDownAction ? (
          <div
            className={styles['topic-card-footer-move-controls']}
            data-testid="topic-card-footer-move-controls"
          >
            {moveUpAction ? (
              <IconButtonV2
                aria-label="Move Up"
                className={classNames(styles['topic-card-footer-move-button'], styles['is-up'])}
                data-testid="topic-card-footer-move-up-button"
                disabled={moveUpAction?.disabled}
                icon={
                  <span className={styles['topic-card-footer-move-icon']}>
                    <ArrowRightIcon />
                  </span>
                }
                onClick={moveUpAction?.onClick}
                size="small"
                tooltip={moveUpAction?.tooltip || 'Move Up'}
                tooltipProps={{ placement: 'top' }}
                variant="subtle"
              />
            ) : null}
            {moveDownAction ? (
              <IconButtonV2
                aria-label="Move Down"
                className={classNames(styles['topic-card-footer-move-button'], styles['is-down'])}
                data-testid="topic-card-footer-move-down-button"
                disabled={moveDownAction?.disabled}
                icon={
                  <span className={styles['topic-card-footer-move-icon']}>
                    <ArrowRightIcon />
                  </span>
                }
                onClick={moveDownAction?.onClick}
                size="small"
                tooltip={moveDownAction?.tooltip || 'Move Down'}
                tooltipProps={{ placement: 'top' }}
                variant="subtle"
              />
            ) : null}
          </div>
        ) : null}
      </>
    );
  };

  const renderFooterStatus = () => {
    if (!footerStatus) return null;

    if (footerStatus?.kind === 'run-date') {
      return (
        <span className={styles['topic-card-footer-status']}>
          <span>Run on </span>
          <strong>{footerStatus.dateLabel}</strong>
        </span>
      );
    }

    return (
      <span
        className={classNames(styles['topic-card-footer-status'], {
          [styles['is-emphasized']]: footerStatus?.emphasized,
        })}
      >
        {footerStatus.label}
      </span>
    );
  };

  const footerRightContent = resolvedFooterMessage() || renderFooterStatus();

  return (
    <div className={styles['topic-card-footer-actions']} data-testid="topic-card-footer-actions">
      <div
        className={styles['topic-card-footer-actions-left']}
        data-testid="topic-card-footer-actions-left"
      >
        {topicControls ? renderControlButtons() : renderDefaultButton()}
      </div>
      {footerRightContent ? (
        <div
          className={styles['topic-card-footer-actions-right']}
          data-testid="topic-card-footer-actions-right"
        >
          {footerRightContent}
        </div>
      ) : null}
    </div>
  );
};

TopicCardFooter.propTypes = {
  footerMessage: PropTypes.string,
  footerStatus: PropTypes.oneOfType([
    PropTypes.shape({
      dateLabel: PropTypes.string.isRequired,
      kind: PropTypes.oneOf(['run-date']).isRequired,
    }),
    PropTypes.shape({
      emphasized: PropTypes.bool,
      label: PropTypes.string.isRequired,
    }),
  ]),
  handleToggleVisibility: PropTypes.func.isRequired,
  isExcluded: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isPreviewActive: PropTypes.bool,
  isPreviewCard: PropTypes.bool,
  shouldShowExcludedTopics: PropTypes.bool,
  topicControls: PropTypes.shape({
    primaryAction: PropTypes.shape({
      disabled: PropTypes.bool,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      tooltip: PropTypes.node,
    }),
    secondaryActions: PropTypes.arrayOf(
      PropTypes.shape({
        disabled: PropTypes.bool,
        label: PropTypes.string.isRequired,
        onClick: PropTypes.func,
        tooltip: PropTypes.node,
      })
    ),
  }),
};

export default TopicCardFooter;
